import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import omit from "lodash.omit";
import SingleLineField from "../../../components/forms/SingleLineField";
import Form from "../../../components/forms/utils/Form";
import { App as AntdApp } from "antd";
import { useMutation } from "@apollo/client";
import { UPDATE_PUBLICATION_QUERY } from "../../../utils/api-utils";
import { withAsyncErrorToast } from "../../../utils/withToast";
import { useCurrentUser } from "../../../utils/CurrentUserContext";
import { optionalHttpsUrlRule, noSpaceRule } from "../../../utils/validators";
import UrlSpaceFixButton from "../../../components/forms/UrlSpaceFixButton";
import { useFormContext } from "react-hook-form";
import TrackingPixelField from "../../../components/forms/Campaign/components/TrackingPixelField";

// Custom LandingPageField component with warning
const PublicationLandingPageField: React.FC<{disabled: boolean}> = ({ disabled }) => {
  const { watch, trigger } = useFormContext();
  const landingPage = watch('landingPage') || '';
  const [showWarning, setShowWarning] = useState(false);
  const hasLandingPage = landingPage !== '';

  // Check if landingPage exceeds 40 characters
  useEffect(() => {
    setShowWarning(landingPage.length > 40);
  }, [landingPage]);

  useEffect(() => {
    if (disabled || !hasLandingPage) return;
    trigger('landingPage');
  }, [disabled, hasLandingPage, trigger]);

  return (
    <>
      <SingleLineField
        top={true}
        label="Landing Page"
        htmlfor="landingPage"
        disabled={disabled}
        type="url"
        rules={{
          validate: {
            ...(disabled ? {} : { noSpace: noSpaceRule }),
            format: optionalHttpsUrlRule,
          }
        }}
        footer={<UrlSpaceFixButton name="landingPage" disabled={disabled} requireHttps />}
        maxLetters="100"
      />
      <div className="text-gray-500 text-sm mt-1">Optional: must be a valid URL if provided.</div>
      {showWarning && (
        <div className="text-yellow-600 text-sm mt-1">
          Warning: Landing page URL exceeds 40 characters. Some systems may truncate this URL.
        </div>
      )}
    </>
  );
};

const Details = () => {
  const {user} = useCurrentUser();
  const { message, notification } = AntdApp.useApp();
  const { campaign, publication } = useOutletContext<any>();
  const [updatePublication, { loading: saving }] = useMutation(
    UPDATE_PUBLICATION_QUERY
  );
  const readOnly = campaign.ownerCompanyId !== user.companyId;

  const defaultValues = {
    landingPage: publication.landingPage,
    budget: publication.budget,
    trackingTag: omit(publication.trackingTag, '__typename'),
    publicationPeriod: {
      startDate: new Date(publication.startDate),
      endDate: new Date(publication.endDate),
    },
  };

  const handleSubmit = async (allValues: any, changedValues: any) => {
    const { publicationPeriod, ...rest } = changedValues;

    const record = {
      ...rest,
      ...publicationPeriod,
    };

    await withAsyncErrorToast(
      'Update publication',
      () => updatePublication({ variables: { id: publication._id, record } }),
      undefined,
      notification
    );

    message.success('Details updated');
  };
  return (
    <Form
      defaultValues={defaultValues}
      className="flex-1 relative bg-white rounded-md px-4 py-4"
      submitButtonText="Customize Details"
      onSubmit={handleSubmit}
      readOnly={readOnly}
    >
      <PublicationLandingPageField disabled={readOnly} />
      <TrackingPixelField span={24} disabled={readOnly} />
    </Form>
  );
};

export default Details;
